import type { DialCodeOption } from '@sentra/contracts';
import { dialCodeOptions, normalize } from '@sentra/contracts';
import { Check, ChevronDown } from 'lucide-react';
import { type ReactNode, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/design/components/input';
import { cn } from '@/design/utils';
import { useCombobox } from '@/features/persons/form/combobox';
import { useLocale } from '@/i18n';

/**
 * The international dialling prefix of a telephone number, chosen by country.
 *
 * **It asks for the thing the operator is holding.** A passport says Morocco; it does not say
 * `+212`. Before this control the desk had to know the code, and a Moroccan number typed in its
 * national form into a field whose default region is Spain was refused as not a Spanish number —
 * a correct refusal of a question nobody meant to ask.
 *
 * **The interaction is `useCombobox`, shared with the country field.** Same arrow keys, same Enter,
 * same Escape, same `aria-activedescendant`, same options that ARE the `li`. What differs is only
 * what a row looks like and what counts as a match.
 *
 * **Three spellings reach a country, because three are what people type.** The name folded through
 * `normalize` — the same fold the register's search uses, so `marruecos` finds `Marruecos` — the
 * ISO code an operator is reading off a document, and the dialling digits themselves with or
 * without a `+`, for somebody who already knows the code and does not want to be asked for a
 * country.
 *
 * **Its accessible name is its own**, because the field's `<Label>` belongs to the number box: the
 * refusal and the label point at the half that can be wrong.
 */
export function DialCodeCombobox({
  value,
  callingCode,
  onChange,
}: Readonly<{
  /** The selected ISO region, for the tick. It is UI state: nothing here is ever stored. */
  value: string | null;
  /** The code to show, which is the number's own when it has one. */
  callingCode: string;
  onChange: (region: DialCodeOption) => void;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const listId = useId();

  const options = useMemo(() => dialCodeOptions(locale), [locale]);
  const combobox = useCombobox(options, matching, onChange);

  // `null` means "showing the selection", exactly as in the country field: a half-typed search must
  // not read as having cleared the prefix.
  const text = combobox.query ?? `+${callingCode}`;
  const highlighted = combobox.highlighted;

  return (
    <div className="relative w-28 shrink-0">
      <Input
        role="combobox"
        type="text"
        inputMode="tel"
        autoComplete="off"
        aria-label={t('person.form.phonePrefix')}
        aria-expanded={combobox.open}
        aria-controls={listId}
        aria-autocomplete="list"
        className="pr-8 font-mono"
        data-numeric
        value={text}
        onChange={(event) => {
          combobox.onQueryChange(event.target.value);
        }}
        onFocus={combobox.onFocus}
        onBlur={combobox.onBlur}
        onKeyDown={combobox.onKeyDown}
        {...(combobox.open && highlighted !== null
          ? { 'aria-activedescendant': `${listId}-${highlighted.value}` }
          : {})}
      />
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
      />

      {combobox.open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('person.form.phonePrefixList')}
          className="absolute z-50 mt-1 max-h-64 w-64 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {combobox.matches.length === 0 && (
            // Presentational, because a `listbox` may hold only options and groups; a bare `li`
            // fails `aria-required-children`. Same reasoning as the country field's empty row, and
            // the same sentence, because it says the same thing.
            <li role="presentation" className="px-2 py-1.5 text-sm text-muted-foreground">
              {t('person.form.countryNoMatches')}
            </li>
          )}
          {combobox.matches.map((option) => (
            <li
              key={option.value}
              id={`${listId}-${option.value}`}
              role="option"
              aria-selected={option.value === value}
              // `onMouseDown`, not `onClick`: blur closes the list first otherwise and the click
              // lands on nothing.
              onMouseDown={(event) => {
                event.preventDefault();
                combobox.choose(option);
              }}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                option.value === highlighted?.value && 'bg-accent text-accent-foreground',
              )}
            >
              <Check
                aria-hidden
                className={cn('size-4', option.value === value ? 'opacity-100' : 'opacity-0')}
              />
              <span className="truncate">{option.label}</span>
              <span className="ml-auto font-mono text-xs text-muted-foreground" data-numeric>
                +{option.callingCode}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** How many options the list offers at once, so an unfiltered open is not a wall of two hundred. */
const VISIBLE_MATCHES = 50;

/**
 * The options matching what has been typed: by name, by ISO code, or by the dialling digits.
 *
 * Declared at module level so `useCombobox`'s memo has a stable dependency.
 */
function matching(
  options: readonly DialCodeOption[],
  query: string | null,
): readonly DialCodeOption[] {
  if (query === null || query.trim().length === 0) {
    return options.slice(0, VISIBLE_MATCHES);
  }
  const needle = normalize(query);
  const digits = needle.replace(/\D/gu, '');
  return options
    .filter(
      (option) =>
        normalize(option.label).includes(needle) ||
        normalize(option.value).startsWith(needle) ||
        (digits.length > 0 && option.callingCode.startsWith(digits)),
    )
    .slice(0, VISIBLE_MATCHES);
}
